import type {ReactElement} from 'react';
import {useRef} from 'react';
import {useAppDispatch} from '@/hooks';
import {CalendarDateTimePicker} from '@Components/calendar-date-time-picker';
import {Popover} from '@Components/popover';
import type {SocialPost} from '@Components/social-media/post.vo';
import {PostState} from '@Components/social-media/post.vo';
import {isToday} from '@Utils/date.util';
import {convertDateToUTCTimestamp} from '@Libraries/social-media-post.library';
import {CACHE_KEY_FOR_SCHEDULING} from '@Libraries/social-media-grid.library';
import {deletePrefilledSchedulingDate} from '@Libraries/social-media-wizard-scheduling-library';
import {updateNextButtonIcon, updateNextButtonText, updatePost} from '@Components/social-media-wizard/social-media-wizard-reducer';
import type {WizardDateTimePickerProps} from './wizard-date-time-picker.types';
import styles from './wizard-date-time-picker.module.scss';

export function WizardDateTimePicker({socialPost, isOpen, onClose}: WizardDateTimePickerProps): ReactElement | null {
  const dispatch = useAppDispatch();
  const ref = useRef<HTMLDivElement>(null);

  const getPrefilledDate = (): Date => {
    const prefilledDate = window.PMW.getCacheItem(CACHE_KEY_FOR_SCHEDULING) as string;

    if (prefilledDate) {
      return new Date(prefilledDate);
    }

    return new Date();
  };

  const shouldSkipDateSelector = (): boolean => {
    return !isToday(getPrefilledDate());
  };

  const onScheduleDetails = (dateTime: Date): void => {
    onClose();
    if (socialPost !== undefined) {
      const timeZoneOffset = dateTime.getTimezoneOffset();
      const newPost: SocialPost = {
        ...socialPost,
        state: PostState.SCHEDULE_READY,
        publishOn: convertDateToUTCTimestamp(dateTime),
        timeZoneOffset,
      };
      dispatch(updatePost(newPost));
      dispatch(updateNextButtonText(window.i18next.t('pmwjs_schedule')));
      dispatch(updateNextButtonIcon('icon-calendar'));
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Popover referenceElementRef={ref} popoverCloseFunction={onClose}>
      <div className={styles.dateTimePickerContainer}>
        <CalendarDateTimePicker
          prefilledDate={getPrefilledDate()}
          skipDateSelector={shouldSkipDateSelector()}
          timePickerCtaText={window.i18next.t('pmwjs_done')}
          onSubmit={(dateTime: Date): void => {
            onScheduleDetails(dateTime);
            deletePrefilledSchedulingDate();
          }}
        />
      </div>
    </Popover>
  );
}
